import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { updateReserveFundPaymentAmount } from "../../helpers/api/finance";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { Toast } from "../UI/toast/Toast";
import ReserveFundAmountFields, {
  useReserveFundAmount,
} from "./ReserveFundAmountFields";

const EditReserveFundAmountModal = ({
  isOpen,
  toggle,
  payment,
  condominiumId,
  onSuccess,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const amountState = useReserveFundAmount({ condominiumId, enabled: isOpen });
  const { amountMode, isBase, appliedExchangeRate, baseAmount, isRateLoading, reset } =
    amountState;
  const initialAmount = payment?.amount ?? payment?.latest_payment?.amount ?? "";

  useEffect(() => {
    if (isOpen) reset(initialAmount);
  }, [isOpen, initialAmount, reset]);

  const handleSave = async () => {
    if (amountMode === "fixed" && !isBase && !(Number(appliedExchangeRate) > 0)) {
      Toast.fire({
        type: "error",
        title: "No hay una tasa disponible para convertir este monto a la moneda base.",
      });
      return;
    }

    if (!(Number(baseAmount) > 0)) {
      Toast.fire({
        type: "error",
        title: "El monto debe ser mayor a 0.",
      });
      return;
    }

    const paymentId =
      payment?.id ||
      payment?.reserve_fund_payment_id ||
      payment?.payment_id ||
      payment?.latest_payment?.id;

    if (!paymentId) {
      Toast.fire({
        type: "error",
        title: "No se encontró el identificador del aporte a editar.",
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateReserveFundPaymentAmount(paymentId, baseAmount);
      Toast.fire({
        type: "success",
        title: "Monto actualizado correctamente.",
      });
      toggle();
      if (onSuccess) await onSuccess();
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Editar monto del aporte</ModalHeader>
      <ModalBody>
        <ReserveFundAmountFields state={amountState} label="Monto" />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={isSaving || (amountMode === "fixed" && !isBase && isRateLoading)}
        >
          {isSaving ? "Guardando..." : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

EditReserveFundAmountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  payment: PropTypes.object,
  condominiumId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
};

EditReserveFundAmountModal.defaultProps = {
  payment: null,
  condominiumId: null,
  onSuccess: null,
};

export default EditReserveFundAmountModal;
